import React, { useState, useEffect, useCallback } from 'react';
import { Button, Table, Modal, Form, Input, Select, DatePicker, message, Space, Card, Typography, Row, Col, Statistic, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import type { TableProps, TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'antd/es/table/interface';
import type { FilterValue } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import api from '../utils/axios';
import { handleApiError } from '../utils/errorHandler';
import { useAuth } from '../contexts/AuthContext';
import UserImport from './UserImport';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  tenantId: string;
  created_at: string;
  updated_at: string;
}

interface Metrics {
  totalUsers: number;
  adminUsers: number;
  regularUsers: number;
}

const UserManagement: React.FC = (): JSX.Element => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Total ${total} items`,
  });
  const [metrics, setMetrics] = useState<Metrics>({
    totalUsers: 0,
    adminUsers: 0,
    regularUsers: 0
  });
  const [showImportModal, setShowImportModal] = useState(false);

  const { user: currentUser } = useAuth();

  const fetchUsers = useCallback(async () => {
    if (!currentUser || currentUser.role !== 'ADMIN') {
      setError('You do not have permission to view users');
      return;
    }

    console.log('Current user:', currentUser);
    console.log('Making API call to /users');
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<{ success: boolean; data: User[] }>('/users', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          search: searchText,
          startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: dateRange?.[1]?.format('YYYY-MM-DD')
        }
      });
      console.log('API response:', response);
      if (response.data.success) {
        setUsers(response.data.data);
        calculateMetrics(response.data.data);
      } else {
        setError('Failed to fetch users');
        message.error('Failed to fetch users');
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch users';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchText, dateRange, currentUser]);

  const calculateMetrics = (data: User[]) => {
    setMetrics({
      totalUsers: data.length,
      adminUsers: data.filter(user => user.role === 'ADMIN').length,
      regularUsers: data.filter(user => user.role === 'USER').length
    });
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEdit = (user: User) => {
    if (!currentUser || currentUser.role !== 'ADMIN') {
      message.error('You do not have permission to edit users');
      return;
    }
    setEditingUser(user);
    setIsModalVisible(true);
    form.setFieldsValue(user);
  };

  const handleDelete = async (id: string) => {
    if (!currentUser || currentUser.role !== 'ADMIN') {
      message.error('You do not have permission to delete users');
      return;
    }
    try {
      await api.delete(`/users/${id}`);
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete user';
      message.error(errorMessage);
    }
  };

  const handleCreate = () => {
    if (!currentUser || currentUser.role !== 'ADMIN') {
      message.error('You do not have permission to create users');
      return;
    }
    setEditingUser(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = async () => {
    if (!currentUser || currentUser.role !== 'ADMIN') {
      message.error('You do not have permission to manage users');
      return;
    }
    try {
      const values = await form.validateFields();
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, values);
        message.success('User updated successfully');
      } else {
        await api.post('/users', values);
        message.success('User created successfully');
      }
      setIsModalVisible(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save user';
      message.error(errorMessage);
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
      sorter: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'ADMIN' ? 'red' : 'blue'}>
          {role}
        </Tag>
      ),
      sorter: true,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
            disabled={!currentUser || currentUser.role !== 'ADMIN'}
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            size="small"
            danger
            disabled={!currentUser || currentUser.role !== 'ADMIN'}
          />
        </Space>
      ),
    },
  ];

  const handleTableChange: TableProps<User> = (pagination, filters, sorter) => {
    if (pagination) {
      setPagination(pagination);
    }
  };

  if (!currentUser || currentUser.role !== 'ADMIN') {
    return (
      <div className="p-6">
        <Card>
          <Typography.Title level={4}>Access Denied</Typography.Title>
          <Typography.Text>You do not have permission to view this page.</Typography.Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Users"
              value={metrics.totalUsers}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Admin Users"
              value={metrics.adminUsers}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Regular Users"
              value={metrics.regularUsers}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="mt-6">
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>Users</Title>
          <Space>
            <Input
              placeholder="Search users..."
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <RangePicker
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                } else {
                  setDateRange(null);
                }
              }}
              allowClear
            />
            <Space style={{ marginBottom: 16 }}>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                Add User
              </Button>
              <Button
                type="primary"
                icon={<UploadOutlined />} 
                onClick={() => setShowImportModal(true)}
              >
                Import Users
              </Button>
            </Space>
          </Space>
        </div>

        {error && (
          <div className="mb-4">
            <Typography.Text type="danger">{error}</Typography.Text>
          </div>
        )}

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          rowSelection={{
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) => {
              setSelectedRowKeys(newSelectedRowKeys);
            },
          }}
        />
      </Card>

      <Modal
        title={editingUser ? 'Edit User' : 'New User'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please enter password' }]}
            >
              <Input.Password />
            </Form.Item>
          )}

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select>
              <Option value="ADMIN">Admin</Option>
              <Option value="USER">User</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <UserImport
        visible={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          setShowImportModal(false);
          fetchUsers();
        }}
      />
    </div>
  );
};

export default UserManagement; 